import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";

const deleteInvestorCommunication = withErrorHandling(adminApi.deleteInvestorCommunication);

interface Props {
  investorCommunicationId: string;
  investorCommunicationTitle: string;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteInvestorCommunicationDialog = ({
  investorCommunicationId,
  investorCommunicationTitle,
  onClose,
  onDeleted,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    const [, error] = await deleteInvestorCommunication(investorCommunicationId);
    setSaving(false);

    if (error) {
      logError(error, "[DeleteInvestorCommunicationDialog] deleteInvestorCommunication");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Investor communication deleted successfully");
    onDeleted();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Delete Investor Communication</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography noWrap variant="h6">
              Delete investor communication?
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              You&apos;re about to delete investor communication <strong>{investorCommunicationTitle}</strong>.<br />{" "}
              This action is permanent and can&apos;t be undone.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={saving}>
        <Button variant="outlined" color="error" loading={saving} onClick={handleSubmit}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteInvestorCommunicationDialog;
